import { OfferContainerProps as PurchaseContainerProps, Identifier, UserInfo } from '@types';
import { logApiEvent, logUserEvent } from '@analytics/amplifyLogging';
import { auth, config as publicConfig } from '../config';
import React from 'react';
import { AuthenticatedLeadForms, AuthenticatedLeadFormsProps, EventType, mobileMask, FormatWithMask } from 'shared-ui-components';
import { selectLoan, selectUserInfo } from '@store/selectors/widgets.selectors';
import { useDispatch, useSelector } from 'react-redux';
import { FormWrapper } from '@app.styled';
import { broadcastCashOutResponse } from '@store/actions/widgets.actions';
import { CodingConstants } from '@constants';
import { addressFormat } from '@utils/addressFormat';

export const PurchaseContainer: React.FC<PurchaseContainerProps> = ({ loanNumber, updateLoanType, offerDetails }) => {
    const dispatch = useDispatch();
    const token = auth.getOkta().getAccessToken();
    const userInfo: UserInfo = useSelector(selectUserInfo);
    const loanList: Identifier[] = useSelector(selectLoan);
    const selectedLoan = loanList.find(loanList => loanList.identifier === loanNumber);
    const { address1, address2, city, state, zip } = selectedLoan.propertyAddress;
    const { email, firstName, lastName, mobile } = userInfo;

    const config: any = {
        metadata: {
            steps: 5,
            type: 'AuthPurchase',
            keyword: 'PurchaseWidget'
        },
        1: {
            screen: 'RadioCardGroup',
            metadata: {
                id: 'visited-buying-time',
                title: 'Where are you in the buying process?',
                navigation: {
                    current: 1,
                    next: 2,
                    back: 1
                },
                valueKey: 'timeFrame',
                metadata: [
                    {
                        id: 'Immediately',
                        icon: 'UnderContractIcon',
                        label: 'Immediately, Contract in-hand',
                        value: 'Immediately: Contract in Hand'
                    },
                    {
                        id: 'Within30Days',
                        icon: 'ThirtyDaysIcon',
                        label: 'Within 30 Days',
                        value: 'Within 30 Days'
                    },
                    {
                        id: '2To3Months',
                        icon: 'TwoThreeMonthsIcon',
                        label: '2-3 Months',
                        value: '2-3 Months'
                    },
                    {
                        id: '3PlusMonths',
                        icon: 'ThreePlusMonthsIcon',
                        label: '3+ Months',
                        value: '3+ Months'
                    }
                ]
            }
        },
        2: {
            screen: 'LocationInformation',
            metadata: {
                id: 'visited-property-state',
                title: 'Where are you looking to purchase your home?',
                navigation: {
                    current: 2,
                    next: 3,
                    back: 1
                }
            }
        },
        3: {
            screen: 'RadioCardGroup',
            metadata: {
                id: 'visited-property-type',
                title: 'What kind of home are you planning to buy?',
                navigation: {
                    current: 3,
                    next: 4,
                    back: 2
                },
                valueKey: 'homeType',
                metadata: [
                    {
                        id: 'Primary',
                        icon: 'PrimaryHomeIcon',
                        label: 'Primary',
                        value: 'PRIMARY'
                    },
                    {
                        id: 'SecondHome',
                        icon: 'SecondHomeIcon',
                        label: 'Second Home',
                        value: 'SECONDARY'
                    },
                    {
                        id: 'Investment',
                        icon: 'InvestmentIcon',
                        label: 'Investment',
                        value: 'INVESTMENT'
                    }
                ],
                enableFooter: true
            }
        },
        4: {
            screen: 'RadioCardGroup',
            metadata: {
                id: 'visited-property-unit-type',
                title: 'What property type are you planning to purchase?',
                navigation: {
                    current: 4,
                    next: 5,
                    back: 3
                },
                valueKey: 'propertyType',
                metadata: [
                    {
                        id: 'SingleFamily',
                        icon: 'SingleFamilyIcon',
                        label: 'Single family home',
                        value: 'SINGLEFAMILY'
                    },
                    {
                        id: 'Condo',
                        icon: 'CondoIcon',
                        label: 'Condo',
                        value: 'CONDO'
                    },
                    {
                        id: 'TownHome',
                        icon: 'TownHomeIcon',
                        label: 'Townhome',
                        value: 'TOWNHOUSE'
                    },
                    {
                        id: 'MultiFamily',
                        icon: 'MultiFamilyHomeIcon',
                        label: 'Multi family home',
                        value: 'MULTIFAMILY'
                    },
                    {
                        id: 'Manufactured',
                        icon: 'ManufacturedIcon',
                        label: 'Manufactured or Mobile home',
                        value: 'MANUFACTURED'
                    }
                ],
                enableFooter: true
            }
        },
        5: {
            screen: 'ReviewInformation',
            metadata: {
                id: 'purchase-review-information',
                title: 'Review and Submit Information',
                navigation: {
                    current: 5,
                    back: 4
                },
                metadata: {
                    reviewInformationList: [
                        {
                            id: 1,
                            title: 'Loan Type',
                            isEdit: false,
                            stepNumber: 0,
                            valueKey: ['refinanceType']
                        },
                        {
                            id: 2,
                            title: 'Timeframe',
                            isEdit: true,
                            stepNumber: 1,
                            valueKey: ['timeFrame']
                        },
                        {
                            id: 3,
                            title: 'Location',
                            isEdit: true,
                            stepNumber: 2,
                            isHeaderNeeded: true,
                            headerValue: 'State',
                            valueKey: ['propertyStateName']
                        },
                        {
                            id: 4,
                            title: 'Home Type',
                            isEdit: true,
                            stepNumber: 3,
                            valueKey: ['homeType']
                        },
                        {
                            id: 4,
                            title: 'Property Type',
                            isEdit: true,
                            stepNumber: 4,
                            valueKey: ['propertyType']
                        },
                        {
                            id: 5,
                            title: 'Contact Information',
                            isEdit: true,
                            stepNumber: 6,
                            valueKey: ['fullName', 'email', 'formattedPhoneNumber', 'propertyInformation']
                        }
                    ]
                }
            }
        },
        6: {
            screen: 'ContactInformation',
            metadata: {
                navigation: {
                    current: 6,
                    back: 5,
                    next: 5
                }
            }
        }
    };
    const purchaseProps: AuthenticatedLeadFormsProps = {
        config,
        accessToken: token,
        logApiEvent: (event: EventType) => {
            logApiEvent({
                apiName: event.apiName,
                action: event.action,
                correlationId: event.correlationId,
                timestamp: event.timestamp,
                loanNumber: event.loanNumber,
                description: event.description,
                type: 'Purchase widget'
            });
        },
        logUserEvent: (event: EventType) => {
            logUserEvent({
                type: 'Purchase widget',
                action: event.action,
                loanNumber: event.loanNumber,
                description: event.description
            });
        },

        metadata: {
            initialState: {
                0: { refinanceType: 'Purchase' },
                1: { timeFrame: '' },
                2: { propertyState: '' },
                3: null,
                4: null,
                6: {
                    fullName: `${firstName} ${lastName}`,
                    formattedPhoneNumber: FormatWithMask({
                        mask: mobileMask,
                        text: mobile
                    }).masked,
                    firstName: firstName,
                    lastName: lastName,
                    email: email,
                    phoneNumber: mobile,
                    propertyInformation: addressFormat(selectedLoan.propertyAddress),
                    address: {
                        street: address1,
                        street2: address2,
                        city: city,
                        state: state,
                        zipCode: zip
                    }
                }
            },
            request: {
                offerGUID: offerDetails.offerGUID,
                borrowers: [
                    {
                        address: {
                            street: address1,
                            street2: address2,
                            city: city,
                            state: state,
                            zipCode: zip
                        },
                        email: email,
                        firstName: firstName,
                        lastName: lastName,
                        middleName: '',
                        nameSuffix: '',
                        phone: mobile
                    }
                ],
                loanNumber: loanNumber,
                productInfo: {
                    type: 'purchase',
                    purchaseDetails: {
                        type: 'PU',
                        puDetails: {
                            timeFrame: '',
                            purchasePrice: 0,
                            homeType: undefined,
                            usage: undefined,
                            zipCode: '',
                            state: ''
                        }
                    }
                },
                referralSource: 'ACP',
                utmDetails: {
                    utmDevice: 'Web',
                    utmDevicemodel: 'Chrome'
                }
            }
        },

        leadApiParams: {
            baseUrl: publicConfig.env('leadSubmissionUrl'),
            xApiKey: publicConfig.env('xApiKey')
        },

        onComplete: (metadata: any) => {
            updateLoanType(CodingConstants.servicingLoanType);
            dispatch(broadcastCashOutResponse(metadata));
        }
    };
    return (
        <FormWrapper>
            <AuthenticatedLeadForms {...purchaseProps} />
        </FormWrapper>
    );
};
